import React, { useContext, useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { LoadSaveService } from 'gw-capability-quoteandbind';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { MLPortalStorageService,MLMultiLobsDataHolderService } from 'ml-services-common-react';
import { MLModalService } from 'ml-portals-services-react';
import { platformMessages } from 'ml-components-platform-react';
import { MLProductCodeUtil, MLDisclosureUtil } from 'ml-portals-util-js';
import { MLDriverVehicleUtil, MLNotificationBlockUtil } from 'ml-portals-util-react';
import { LocalDateUtil } from '@xengage/gw-portals-util-js';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import metadata from './AdjustmentsPage.metadata.json5';
import messages from './AdjustmentsPage.messages';
import styles from './AdjustmentsPage.module.scss'

import { IntlContext } from '@jutro/legacy/locale';

const {
    ML_STORAGE_KEY_PREVIOUS_PREMIUM
} = appConfig;


const getMonthlyCost = (offeredQuote) => {
    return offeredQuote ? offeredQuote.premium.monthlyPremium : 0;
};

const getAnnualCost = (offeredQuote) => {
    return offeredQuote ? offeredQuote.premium.total : 0;
};

const getViolationDescription = (violation) => {
    if (violation.type) {
        return violation.type.replace(/_/g, ' ');
    }
    return violation.category;
};

function AdjustmentsPage(props) {
    const {
        wizardData: submissionVM,
        goNext,
        steps,
        jumpTo,
        isSkipping
    } = props;
    const intl = useContext(IntlContext);
    const translator = useTranslator();

    const {
        showProgressDialog
    } = MLModalService();
    const {
        addWarningAlert,
        addErrorAlert
    } = MLNotificationBlockUtil();

    const {
        checkHiDrivers,
        checkPendingMvrOrderStatus,
    } = MLDriverVehicleUtil();

    const isPersonalAuto = MLProductCodeUtil.isPersonalAuto(submissionVM.baseData.productCode.value);
    const isPersonalUmbrella = MLProductCodeUtil.isPersonalUmbrella(submissionVM.baseData.productCode.value);

    const isQnB = appConfig.portalCode === 'QB';

    const getSelectedQuote = useCallback(() => {
        const selectedQuoteId = _.get(submissionVM, 'bindData.chosenQuote.value');
        const offeredQuotes = _.get(submissionVM, 'quoteData.offeredQuotes.value', []);
        return offeredQuotes.find((q) => q.publicID === selectedQuoteId);
    }, [submissionVM]);

    const populateViolations = useCallback(() => {
        if (isPersonalAuto) {
            const drivers = _.get(submissionVM, 'lobData.personalAuto.coverables.drivers.value', []);
            return drivers.flatMap((driver) => driver.disclosures_ML.violations)
                .filter((violation) => violation.undisclosedActivity_ML === true && violation.category === 'violation');
        }

        if (isPersonalUmbrella) {
            const drivers = _.get(submissionVM, 'lobData.personalUmbrella.coverables.vehicleOperators.value', [])
                .filter((driver) => driver.driverType === 'rated');
            return drivers.flatMap((driver) => driver.disclosures.violations)
                .filter((violation) => violation.undisclosedActivity_ML === true && violation.category === 'violation');
        }

        return [];
    }, [isPersonalAuto, isPersonalUmbrella, submissionVM]);

    const getFormattedDateString = useCallback((gwDateVal) => {
        const date = LocalDateUtil.toMidnightDate(gwDateVal);
        return intl.formatDate(date, { year: 'numeric', month: '2-digit', day: '2-digit' });
    }, [intl]);

    const selectedQuote = getSelectedQuote();
    const currentMonthlyCost = getMonthlyCost(selectedQuote);
    const currentAnnualCost = getAnnualCost(selectedQuote);
    const violations = populateViolations();
    const [previousMonthlyCost, setPreviousMonthlyCost] = useState(0);
    const [previousAnnualCost, setPreviousAnnualCost] = useState(0);

    const getFormerCosts = useCallback(() => {
        const storagePromise = MLPortalStorageService.retrieve(submissionVM.quoteID.value, null, ML_STORAGE_KEY_PREVIOUS_PREMIUM);
        showProgressDialog(platformMessages.synchronizingQuote, storagePromise);
        storagePromise.then((response) => {
            if (response) {
                const formerCosts = JSON.parse(response);
                const matchingQuote = formerCosts.find((quote) => quote.branchCode === selectedQuote.branchCode);
                if (matchingQuote) {
                    setPreviousMonthlyCost(_.get(matchingQuote, 'monthlyPremium', 0));
                    setPreviousAnnualCost(_.get(matchingQuote, 'total', 0));
                }
            }
        });
        storagePromise.finally(() => {
            MLDisclosureUtil.setRequoteAfterMvr(submissionVM.quoteID.value, true);
        });
    }, [selectedQuote.branchCode, showProgressDialog, submissionVM.quoteID.value]);

    const showPremiumChangedAlert = useCallback(() => {
        if (!_.isEmpty(violations) && (isPersonalAuto || isPersonalUmbrella)) {
            addWarningAlert(messages.receivedAdditionalInfoAdjustedPremium, 'mvrActivityAlert');
        } else if (isPersonalAuto) {
            addWarningAlert(messages.premiumChangedDueToInfoReceived, 'premiumChangedAlert');
        }
    }, [addWarningAlert, isPersonalAuto, isPersonalUmbrella, violations]);


    const showHIDriverMVRReportPendingAlert = useCallback(() => {
        if (isPersonalAuto) {
            addErrorAlert(messages.orderMVRforHIDriver, 'HIDriverMVRPendingAlert');
        }
    }, [addErrorAlert, isPersonalAuto]);

    const skipOnResumeIf = useCallback(() => {
        // Skip to Confirmation Page for policy with a policy number
        if (!!_.get(submissionVM, 'bindData.policyNumber.value')
            && (submissionVM.value.baseData.status_ML === 'Bound' || submissionVM.value.baseData.status_ML === 'Binding')) {
            return true;
        }

        return submissionVM.isBundle_ML.value && submissionVM.value.baseData.status_ML !== 'Draft' &&
        (MLMultiLobsDataHolderService.isBeforeLastPurchaseFlowStepVisited('paDisclosureReviewToBuy', submissionVM.value) &&
            MLMultiLobsDataHolderService.isBeforeLastPurchaseFlowStepVisited('paVeracityCheck', submissionVM.value));
    }, [submissionVM]);


    const displayHIDriverWarning = () => {
        const drivers = isPersonalAuto ? _.get(submissionVM, 'lobData.personalAuto.coverables.drivers.value') : _.get(submissionVM, 'lobData.personalUmbrella.coverables.vehicleOperators.value');
        if( drivers && checkHiDrivers (drivers) && checkPendingMvrOrderStatus(drivers))
            return true;
        return false;    
    } 


    useEffect(() => {
        if (!isSkipping || (isSkipping && !skipOnResumeIf())) {
            getFormerCosts();
            showPremiumChangedAlert();
            if(isPersonalAuto && displayHIDriverWarning())
                showHIDriverMVRReportPendingAlert();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSkipping]);

    const onNext = useCallback(() => {
        const promise = LoadSaveService.updateQuotedSubmission(submissionVM.value);
        showProgressDialog(platformMessages.saveSubmission, promise);
        promise.then((res) => {
            submissionVM.value = res;
            goNext();
        });
    }, [goNext, showProgressDialog, submissionVM]);

    const goToQuoteDetails = useCallback(() => {
        const index = _.findIndex(
            steps,
            ({ path }) => path === '/quote'
        );
        jumpTo(index);
    }, [jumpTo, steps]);

    const resolvers = {
        resolveCallbackMap: {
            onNext,
            goToQuoteDetails
        }
    };

    const generateViolationsOverrides = () => {
        const overrides = violations.map((violation, index) => {
            return {
                [`violation${index}`]: {
                    content: <FormattedMessage
                        {...messages.violationDetails}
                        values={{
                            person: <strong>{violation.person.displayName}</strong>,
                            date: getFormattedDateString(violation.date),
                            description: getViolationDescription(violation)
                        }}
                    />
                }
            };
        });

        return Object.assign({}, ...overrides);
    };

    const overrides = {
        violations: {
            data: violations
        },
        insuranceType: {
            content: translator(isPersonalAuto ? messages.autoInsurance : messages.umbrellaInsurance)
        },
        currentMonthlyPremiumContainer: {
            visible: _.has(selectedQuote, 'premium.monthlyPremium')
        },
        currentMonthlyPremiumValue: {
            showFractions: true,
            tag: 'span',
            currency: currentMonthlyCost.currency,
            value: currentMonthlyCost.amount,
            suffix: ' '
        },
        currentAnnualPremiumValue: {
            showFractions: true,
            tag: 'span',
            currency: currentAnnualCost.currency,
            value: currentAnnualCost.amount,
            suffix: ' '
        },
        yearInterval: {
            content: translator(isQnB ? messages.sixMonths : messages.year)
        },
        previousMonthlyPremium: {
            className: isQnB ? "body-copy secondary text-primary-color" : "body-copy secondary light"
        },
        previousMonthlyPremiumValue: {
            showFractions: true,
            tag: 'span',
            currency: previousMonthlyCost.currency,
            value: previousMonthlyCost.amount,
            prefix: ' ',
            suffix: ' '
        },
        previousAnnualPremiumValue: {
            showFractions: true,
            tag: 'span',
            currency: previousAnnualCost.currency,
            value: previousAnnualCost.amount,
            prefix: ' ',
            suffix: ' '
        },
        previousYearInterval: {
            content: translator(isQnB ? messages.sixMonths : messages.year)
        },
        stateFeesComponent: {
            visible: _.get(selectedQuote, 'totalChanges_Ext.amount', 0) !== 0,
            totalFees: _.get(selectedQuote, 'totalChanges_Ext', {}),
            itemizedFees: _.get(selectedQuote, 'feesCosts_Ext', []),
            displayAlternativeTemplate: true
        },
        nextDescription: {
            content: (
                <FormattedMessage
                    {...messages.reviewPaymentDetails}
                    values={{
                        paymentDetails: (
                            <strong>{translator(messages.paymentDetails)}</strong>
                        )
                    }}
                />
            )
        },
        adjustedPremiumHeader: {
            className: isQnB ? "page-header vertical-spacing-20 headerOne" : "page-header vertical-spacing-20"
        },
        nextButton : {
            disabled: displayHIDriverWarning(),
            className: isQnB ? "primaryButton" : ""
        },
        ...generateViolationsOverrides()
    };

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            showNext={false}
            skipWhen={skipOnResumeIf}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={styles}
            />
        </WizardPage>
    );
}

AdjustmentsPage.propTypes = wizardProps;
export default AdjustmentsPage;
